/**
 * SNS 계정 정규화/검증/프로필 URL 유틸
 *
 * 신청 폼(apply)과 대시보드(seeding-campaigns) 양쪽에서 재사용한다.
 * - normalize: URL, @핸들, 핸들 등 다양한 입력을 핸들/ID만으로 정규화
 * - validate: 정규화된 값이 올바른 형식인지 검증
 * - toProfileUrl: 저장된 값(핸들 또는 레거시 URL)을 프로필 URL로 변환
 */

interface SnsChannelDef {
  name: string
  placeholder: string
  /** 입력값에서 핸들/ID를 추출 (URL, @ 제거) */
  normalize: (input: string) => string
  /** 정규화된 값이 유효한 형식인지 검증 (rawInput: 정규화 전 원본 입력값) */
  validate: (handle: string, rawInput?: string) => boolean
  /** DB 저장값 → 프로필 URL. 기존 레거시(URL 형태) 값도 처리 */
  toProfileUrl: (account: string) => string | null
}

export const SNS_CHANNELS: Record<string, SnsChannelDef> = {
  instagram: {
    name: '인스타그램',
    placeholder: '@seoyeon_k',
    normalize(input: string): string {
      let v = input.trim()
      // URL에서 핸들 추출: instagram.com/handle 또는 instagram.com/handle/
      const urlMatch = v.match(/(?:instagram\.com|instagr\.am)\/([a-zA-Z0-9._]+)\/?/)
      if (urlMatch) return urlMatch[1]
      // @ 제거
      if (v.startsWith('@')) v = v.slice(1)
      return v
    },
    validate(handle: string): boolean {
      return /^[a-zA-Z0-9._]{1,30}$/.test(handle)
    },
    toProfileUrl(account: string): string | null {
      if (!account) return null
      // 이미 URL 형태면 https:// 붙여서 반환
      if (account.includes('instagram.com')) {
        return account.startsWith('http') ? account : `https://${account}`
      }
      const handle = account.startsWith('@') ? account.slice(1) : account
      return `https://instagram.com/${handle}`
    },
  },

  blog: {
    name: '네이버 블로그',
    placeholder: 'blog.naver.com/seoyeon',
    normalize(input: string): string {
      let v = input.trim()
      // URL에서 ID 추출: blog.naver.com/id 또는 m.blog.naver.com/id
      const urlMatch = v.match(/(?:m\.)?blog\.naver\.com\/([a-zA-Z0-9_-]+)\/?/)
      if (urlMatch) return urlMatch[1]
      // https:// 등 스킴 제거 후 다시 시도
      v = v.replace(/^https?:\/\//, '')
      const match2 = v.match(/(?:m\.)?blog\.naver\.com\/([a-zA-Z0-9_-]+)\/?/)
      if (match2) return match2[1]
      return v
    },
    validate(id: string, rawInput?: string): boolean {
      // blog.naver.com URL 형태만 허용
      if (rawInput) {
        const cleaned = rawInput.trim().replace(/^https?:\/\//, '')
        if (!cleaned.match(/^(?:m\.)?blog\.naver\.com\/[a-zA-Z0-9_-]+\/?$/)) return false
      }
      return /^[a-zA-Z0-9_-]{1,30}$/.test(id)
    },
    toProfileUrl(account: string): string | null {
      if (!account) return null
      // 이미 URL 형태면 https:// 붙여서 반환
      if (account.includes('blog.naver.com')) {
        return account.startsWith('http') ? account : `https://${account}`
      }
      return `https://blog.naver.com/${account}`
    },
  },

  tiktok: {
    name: '틱톡',
    placeholder: '@seoyeon',
    normalize(input: string): string {
      let v = input.trim()
      // URL에서 핸들 추출: tiktok.com/@handle
      const urlMatch = v.match(/tiktok\.com\/@([a-zA-Z0-9._]+)\/?/)
      if (urlMatch) return urlMatch[1]
      // @ 제거
      if (v.startsWith('@')) v = v.slice(1)
      return v
    },
    validate(handle: string): boolean {
      return /^[a-zA-Z0-9._]{1,24}$/.test(handle)
    },
    toProfileUrl(account: string): string | null {
      if (!account) return null
      if (account.includes('tiktok.com')) {
        return account.startsWith('http') ? account : `https://${account}`
      }
      const handle = account.startsWith('@') ? account.slice(1) : account
      return `https://www.tiktok.com/@${handle}`
    },
  },

  youtube: {
    name: '유튜브',
    placeholder: '@seoyeon',
    normalize(input: string): string {
      let v = input.trim()
      // URL에서 핸들 추출: youtube.com/@handle
      const urlMatch = v.match(/youtube\.com\/@([a-zA-Z0-9._-]+)\/?/)
      if (urlMatch) return urlMatch[1]
      // @ 제거
      if (v.startsWith('@')) v = v.slice(1)
      return v
    },
    validate(handle: string): boolean {
      return /^[a-zA-Z0-9._-]{1,30}$/.test(handle)
    },
    toProfileUrl(account: string): string | null {
      if (!account) return null
      if (account.includes('youtube.com')) {
        return account.startsWith('http') ? account : `https://${account}`
      }
      const handle = account.startsWith('@') ? account.slice(1) : account
      return `https://www.youtube.com/@${handle}`
    },
  },
}

/** 입력값을 정규화 (핸들/ID만 추출). 미지원 채널은 원본 반환 */
export function normalizeSnsAccount(channel: string, input: string): string {
  const def = SNS_CHANNELS[channel]
  if (!def) return input.trim()
  return def.normalize(input)
}

/** 정규화된 값이 유효한 형식인지 검증. 미지원 채널은 항상 true */
export function validateSnsAccount(channel: string, normalized: string, rawInput?: string): boolean {
  const def = SNS_CHANNELS[channel]
  if (!def) return true
  return def.validate(normalized, rawInput)
}

/** DB 저장값 → 프로필 URL. 미지원 채널이거나 빈 값이면 null */
export function getSnsProfileUrl(channel: string, accountValue: string): string | null {
  if (!accountValue) return null
  const def = SNS_CHANNELS[channel]
  if (!def) return null
  return def.toProfileUrl(accountValue)
}
